<template>
  <BittsModal
    :width="500"
    :loading="isSaving"
    :action-loading="isSaving"
    :use-mask-to-close="true"
    title="Create Integration"
    variant="data"
    primary-button-text="Create Integration"
    :disabled="!canCreate"
    @closed="cancel"
    @action="createApplication"
  >
    <div ref="form">
      <BittsInput
        v-model="oAuthApplication.name"
        form-label="Integration Name"
        class="mb-24"
        placeholder="Integration Name"
        name="integration-name"
        :status="
          v$.oAuthApplication.name.$errors?.length ? 'danger' : 'default'
        "
        :danger-text="v$.oAuthApplication.name.$errors?.at(-1)?.$message || ''"
      />
      <BittsInput
        v-model="oAuthApplication.description"
        class="mb-24"
        form-label="Integration Description"
        placeholder="Integration Description"
        name="integration-description"
      />
      <div class="c-edit-application__modal-body mb-24">
        <span class="c-edit-application__label">Callback URL</span>
        <BittsSelectTags
          v-model="oAuthApplication.callbacks"
          :warning-message="
            showCallbackUrlsMessage ? CALLBACK_URLS_MESSAGE : ''
          "
          note="Note: You can add separate callback URLs by separating with enter"
          placeholder="Callback URLs"
          class="c-edit-application__tags-input"
          @change="onChangeCallbackUrls"
          @search="inProgressCallbackUrl = $event"
        />
      </div>
      <div class="c-edit-application__modal-body">
        <span class="c-edit-application__label">Allowed Origins</span>
        <BittsSelectTags
          v-model="oAuthApplication.allowed_origins"
          note="Note: You can add separate allowed origins by separating with enter"
          :warning-message="
            showAllowedOriginsMessage ? ALLOWED_ORIGINS_MESSAGE : ''
          "
          placeholder="Allowed Origins"
          class="c-edit-application__tags-input"
          @change="onChangeAllowedOrigins"
          @search="inProgressAllowedOrigin = $event"
        />
      </div>
      <p class="c-edit-application__developer-terms">
        Please note: By clicking "Create Integration" below, you agree to the
        Crossbeam Developer Terms. Please review the
        <BittsLink
          text="Developer Terms here"
          url="https://www.crossbeam.com/legal/developers/"
          :open-in-new-tab="true"
        />
      </p>
    </div>
  </BittsModal>
</template>

<script setup lang="ts">
import {
  BittsInput,
  BittsLink,
  BittsModal,
  BittsSelectTags,
} from '@crossbeam/bitts';

import { useHead } from '@unhead/vue';
import { useVuelidate } from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import type { SelectValue } from 'ant-design-vue/es/select';
import { computed, reactive, ref, useTemplateRef } from 'vue';

import { useFlashesStore, useOauthApplicationStore } from '@/stores';
import { isUrlValid } from '@/utils';
import { focusFirstInvalidField } from '@/utils/focusFirstInvalidField';

const emit = defineEmits<{
  closed: [];
}>();
const CALLBACK_URLS_MESSAGE = 'Callback must start with https://';
const ALLOWED_ORIGINS_MESSAGE =
  'Origin must start with https:// or chrome-extensions://';
const ORIGIN_PROTOCOLS = ['chrome-extensions://'];

// without this, we use the metaInfo from integrations/create.vue even after the modal closes
useHead({});

const flashesStore = useFlashesStore();
const oauthApplicationStore = useOauthApplicationStore();

const oAuthApplication = reactive({
  name: '',
  description: '',
  callbacks: [] as string[],
  allowed_origins: [] as string[],
});

const rules = {
  oAuthApplication: {
    name: {
      required: helpers.withMessage('Please name your application.', required),
    },
  },
};

const v$ = useVuelidate(rules, { oAuthApplication });
const form = useTemplateRef('form');

// Disable rather than letting a click fail, so a missing name is visible up
// front instead of only after submitting.
const canCreate = computed(() => !v$.value.$invalid);

const isSaving = ref(false);
const showCallbackUrlsMessage = ref(false);
const showAllowedOriginsMessage = ref(false);
const inProgressCallbackUrl = ref('');
const inProgressAllowedOrigin = ref('');

function isCallbackValid(tag: string) {
  return isUrlValid(tag, []);
}

function isOriginValid(tag: string) {
  return isUrlValid(tag, ORIGIN_PROTOCOLS);
}

function cancel() {
  emit('closed');
}

function toTags(value: SelectValue) {
  return Array.isArray(value) ? (value as string[]) : [];
}

function onChangeCallbackUrls(value: SelectValue) {
  const values = toTags(value);
  showCallbackUrlsMessage.value = values.some((v) => !isCallbackValid(v));
  oAuthApplication.callbacks = values.filter(isCallbackValid);
  inProgressCallbackUrl.value = '';
}

function onChangeAllowedOrigins(value: SelectValue) {
  const values = toTags(value);
  showAllowedOriginsMessage.value = values.some((v) => !isOriginValid(v));
  oAuthApplication.allowed_origins = values.filter(isOriginValid);
  inProgressAllowedOrigin.value = '';
}

/**
 * BittsSelectTags only commits a tag once the user presses enter, so a value
 * still sitting in the search box would be dropped on submit. Commit it here,
 * or surface the warning and stop if it is not valid.
 */
function commitInProgressTags() {
  if (inProgressCallbackUrl.value) {
    if (!isCallbackValid(inProgressCallbackUrl.value)) {
      showCallbackUrlsMessage.value = true;
      return false;
    }
    oAuthApplication.callbacks.push(inProgressCallbackUrl.value);
  }
  if (inProgressAllowedOrigin.value) {
    if (!isOriginValid(inProgressAllowedOrigin.value)) {
      showAllowedOriginsMessage.value = true;
      return false;
    }
    oAuthApplication.allowed_origins.push(inProgressAllowedOrigin.value);
  }
  return true;
}

async function createApplication() {
  v$.value.$touch();
  if (v$.value.$invalid) {
    await focusFirstInvalidField(form);
    return;
  }
  if (!commitInProgressTags()) return;

  try {
    isSaving.value = true;
    await oauthApplicationStore.createOAuthApplication({
      name: oAuthApplication.name,
      description: oAuthApplication.description,
      callbacks: oAuthApplication.callbacks,
      allowed_origins: oAuthApplication.allowed_origins,
    });
    flashesStore.addSuccessFlash({ message: 'The integration was created' });
    await oauthApplicationStore.refreshOAuthApplicationsStore();
  } catch (_err) {
    flashesStore.addErrorFlash({
      message: 'The integration could not be created',
    });
  } finally {
    isSaving.value = false;
    emit('closed');
  }
}
</script>

<style lang="pcss" scoped>
@reference "../css/theme.css";
.c-edit-application__label {
  @apply text-neutral-text-strong font-bold text-sm mr-4;
}

.c-edit-application__tags-input {
  @apply mt-8 mb-24;
}
.c-edit-application__developer-terms {
  @apply mt-20 text-sm text-neutral-500;
}
.o-bitts-modal.bitts-modal--data:deep() .o-bitts-modal__content {
  @apply m-0 p-24;
}
</style>
